/**
 * Cashfree Drop-in v3 loader + open helper.
 *
 * The Cashfree SDK is a single <script> on a CDN. We lazy-load on first
 * use so /admin or non-payment pages don't pull it. Subsequent calls
 * re-use the already-loaded global.
 *
 * Usage:
 *   const cashfree = await loadCashfree({ mode: "sandbox" });
 *   const result = await openCashfreeCheckout(cashfree, {
 *     paymentSessionId: "session_xxx",
 *     returnUrl: window.location.href,
 *   });
 *
 * SDK API:
 *   cashfree.checkout({paymentSessionId, returnUrl, redirectTarget})
 *     - returnUrl receives a redirect on completion (when redirectTarget="_self")
 *     - With redirectTarget="_modal" the promise resolves with the result
 *       and the page is NOT navigated; we then call our own status API.
 *
 * Mode is driven by VITE_CASHFREE_ENV ("sandbox" | "production").
 * Defaults to "sandbox" so a local build never hits live by accident.
 */

const SDK_SRC = "https://sdk.cashfree.com/js/v3/cashfree.js";

let _cashfreePromise = null;

export function loadCashfree(opts = {}) {
  const mode = (opts.mode || import.meta.env.VITE_CASHFREE_ENV || "sandbox").toLowerCase();
  if (_cashfreePromise) {
    // Re-init the instance with the requested mode in case the caller
    // wants a different env (rare, but cheap).
    return _cashfreePromise.then((cf) => cf || _initCashfree(mode));
  }
  _cashfreePromise = new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve(_initCashfree(mode));
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_SRC;
    s.async = true;
    s.onload = () => {
      if (!window.Cashfree) {
        reject(new Error("Cashfree SDK loaded but global missing"));
        return;
      }
      resolve(_initCashfree(mode));
    };
    s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
    document.head.appendChild(s);
  });
  return _cashfreePromise;
}

function _initCashfree(mode) {
  // global.Cashfree is a factory; mode picks the API host (the SDK
  // internally points sandbox/production at the right endpoints).
  return window.Cashfree({ mode });
}

/**
 * Open Drop-in and wait for it to close. Resolves with the Drop-in
 * result object (.error / .order / .paymentDetails per Cashfree's
 * docs). Does NOT verify payment — that's the webhook's job; the
 * caller should re-fetch wallet/sub state.
 *
 * `mode`:
 *   - "modal" (default) — embed checkout in an iframe on this page.
 *     Requires the SPA's origin to be whitelisted in the Cashfree
 *     merchant settings. Production accounts must add zencricketdata.com
 *     to "Allowed Origins" or the iframe will refuse to render with
 *     "This content is blocked".
 *   - "redirect" — full-page navigate to Cashfree's hosted checkout,
 *     then come back to returnUrl on completion. No origin whitelist
 *     required; works on day-zero production merchants.
 *
 * Choose redirect mode when the merchant hasn't completed origin
 * whitelisting yet, on mobile devices where the modal is awkward, or
 * as a defensive fallback when the modal fails to render.
 */
export function openCashfreeCheckout(cashfree, { paymentSessionId, returnUrl, mode = "modal" }) {
  if (!cashfree) throw new Error("openCashfreeCheckout: cashfree instance missing");
  if (!paymentSessionId) throw new Error("openCashfreeCheckout: paymentSessionId required");

  return cashfree.checkout({
    paymentSessionId,
    redirectTarget: mode === "redirect" ? "_self" : "_modal",
    returnUrl,
  });
}
